import { Directive, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControls, TypedFormGroup } from 'osn-shared';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, takeUntil, tap } from 'rxjs/operators';
import { MultiSelectOption } from '../../shared/components/multi-select/multi-select.component';
import { deepEqual } from '../../shared/functions/deepEqual';
import { TrayFormViewModel } from './view-model-common/tray-form-view-model';

@Directive()
export abstract class TrayFormComponentBase<T extends TrayFormViewModel, FormModel, Controls extends FormControls<unknown>>
  implements OnInit, OnDestroy
{
  private readonly FORM_DEBOUNCE_TIME_MS = 500;
  protected readonly destroyed$ = new Subject<void>();

  public formControls: Controls;
  public form: TypedFormGroup<FormModel>;

  @Input() public value: T;
  @Input() public isReadonly: boolean;
  @Input() public userSegmentOptions: MultiSelectOption[];
  @Input() public countryOptions: MultiSelectOption[];
  @Input() public platformOptions: MultiSelectOption[];
  @Output() public valueChanges: EventEmitter<T> = new EventEmitter();
  @Output() public formValidityChange: EventEmitter<boolean> = new EventEmitter();

  public ngOnInit(): void {
    this.initForm(this.value);
    this.watchFormChanges();
  }

  public ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  protected watchFormChanges() {
    this.form.valueChanges
      .pipe(
        takeUntil(this.destroyed$),
        tap(() => this.formValidityChange.emit(this.form.valid)),
        filter(() => this.form.enabled),
        debounceTime(this.FORM_DEBOUNCE_TIME_MS),
        distinctUntilChanged((a, b) => deepEqual(a, b))
      )

      .subscribe((value: FormModel) => {
        const viewModelValue = this.mapFormModelToViewModel(value);
        this.valueChanges.emit(this.form.valid ? viewModelValue : undefined);
      });
  }

  protected setDisabled(isDisabled: boolean) {
    if (isDisabled) {
      this.form?.disable();
    } else {
      this.form?.enable();
    }
    this.form?.updateValueAndValidity();
  }

  protected abstract initForm(value: T);
  protected abstract mapFormModelToViewModel(value: FormModel): T;
  protected abstract getDefaultVm(): T;
}
